import * as CL from '@design-system/component-library';
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { dsClass } from '../../common/constants/dsClasses';
import { updateCompanyInfo } from '../../common/fetch';
import {
  I18nTextLinkEmbedded,
  auxiliaryEsimProductPriceMsg,
  cancelMsg,
  confirmMsg,
  editMsg,
  payerMsg,
  priceMsg,
  t,
} from '../../common/i18n/index.js';
import { SelectRadio } from '../../common/react-hook-form/components/SelectRadio.js';
import { UpdateAccountRequest } from '../../generated/api/updateAccountRequest';
import { addNotification } from '../../public/site/notificationStore';
import { ConfirmationModal } from '../Modal/ConfirmationModal.js';
import type { CompanyInfoLoaderData } from '../../common/loaders';
import type { SelectRadioItem } from '../../common/react-hook-form/components/SelectRadio.js';

export interface KelloSimBillingModelSettingProps {
  companyInfos: CompanyInfoLoaderData[];
}

export const enum KelloSimPayer {
  DISABLED = 'DISABLED',
  COMPANY = 'COMPANY',
  EMPLOYEE = 'EMPLOYEE',
}

export const getKelloSimPayer = (companyInfo: CompanyInfoLoaderData): KelloSimPayer => {
  if (!companyInfo.allowOrderAdditionalSIM) {
    return KelloSimPayer.DISABLED;
  }
  return companyInfo.additionalSimPayer === UpdateAccountRequest.AdditionalSimPayerEnum.EMPLOYEE
    ? KelloSimPayer.EMPLOYEE
    : KelloSimPayer.COMPANY;
};

export const toUpdateAccountRequest = (
  payer: KelloSimPayer,
  companyInfo: CompanyInfoLoaderData
): UpdateAccountRequest => ({
  blockInvoiceDueSmsReminder: companyInfo.blockInvoiceDueSmsReminder ?? false,
  allowOrderAdditionalSIM: payer !== KelloSimPayer.DISABLED,
  additionalSimPayer:
    payer === KelloSimPayer.DISABLED
      ? undefined
      : payer === KelloSimPayer.EMPLOYEE
        ? UpdateAccountRequest.AdditionalSimPayerEnum.EMPLOYEE
        : UpdateAccountRequest.AdditionalSimPayerEnum.COMPANY,
});

export interface KelloSimPayerFormValues {
  payer: string;
}

export interface KelloSimBillingModelCompanySettingProps {
  companyInfo: CompanyInfoLoaderData;
  showCompanyName: boolean;
}

export const KelloSimBillingModelCompanySetting = ({
  companyInfo,
  showCompanyName,
}: KelloSimBillingModelCompanySettingProps) => {
  const navigate = useNavigate();
  const [isEdit, setIsEdit] = useState(false);
  const [pendingPayer, setPendingPayer] = useState<KelloSimPayer | undefined>(undefined);

  const currentPayer = getKelloSimPayer(companyInfo);

  const methods = useForm<KelloSimPayerFormValues>({
    mode: 'all',
    defaultValues: { payer: currentPayer },
  });

  const {
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  const payerLabel = (payer: KelloSimPayer): string => {
    switch (payer) {
      case KelloSimPayer.COMPANY:
        return companyInfo.companyName || t.PB6S(payerMsg);
      case KelloSimPayer.EMPLOYEE:
        return t.R7FZ('Employee');
      case KelloSimPayer.DISABLED:
      default:
        return t.SSQQ('KelloSIM service is not available');
    }
  };

  const radioItems: SelectRadioItem[] = [
    { label: companyInfo.companyName || t.PB6S(payerMsg), value: KelloSimPayer.COMPANY },
    { label: t.R7FZ('Employee'), value: KelloSimPayer.EMPLOYEE },
    { label: t.SSQQ('KelloSIM service is not available'), value: KelloSimPayer.DISABLED },
  ];

  const submitPayerChange = async (payer: KelloSimPayer) => {
    try {
      await updateCompanyInfo(toUpdateAccountRequest(payer, companyInfo), companyInfo.mdmId);
      setIsEdit(false);
      navigate('');
      addNotification(t.ITOF('The change request was sent successfully'));
    } catch {
      addNotification(t.L7BV('We could not update your changes. Please try again later.'), 'error');
    } finally {
      setPendingPayer(undefined);
    }
  };

  const onSubmit = ({ payer }: KelloSimPayerFormValues) => {
    setPendingPayer(payer as KelloSimPayer);
  };

  const priceValue = t.FKVB(auxiliaryEsimProductPriceMsg);

  return (
    <div className={dsClass.MARGIN_TOP_5}>
      {showCompanyName && <h4>{companyInfo.companyName}</h4>}
      {isEdit ? (
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)} noValidate>
            <CL.Description
              columns={2}
              className={dsClass.MARGIN_LEFT_0}
              items={[
                { title: t.PB6S(payerMsg), description: <SelectRadio name="payer" items={radioItems} /> },
                { title: t.V72N(priceMsg), description: priceValue },
              ]}
            />
            <div className={dsClass.MARGIN_TOP_5}>
              <CL.Button type="submit" loading={isSubmitting}>
                {t.QVYK(confirmMsg)}
              </CL.Button>
              <CL.Button
                color="link"
                onClick={() => setIsEdit(false)}
                disabled={isSubmitting}
                className={dsClass.MARGIN_LEFT_2}
              >
                {t.B2V1(cancelMsg)}
              </CL.Button>
            </div>
          </form>
        </FormProvider>
      ) : (
        <>
          <CL.Description
            columns={2}
            className={dsClass.MARGIN_LEFT_0}
            items={[
              { title: t.PB6S(payerMsg), description: payerLabel(currentPayer) },
              { title: t.V72N(priceMsg), description: priceValue },
            ]}
          />
          <CL.Button color="light" onClick={() => setIsEdit(true)} className={dsClass.MARGIN_TOP_5}>
            {t.NVPK(editMsg)}
          </CL.Button>
        </>
      )}
      {pendingPayer !== undefined && (
        <ConfirmationModal
          heading={t.C9OV('Change the payer for the KelloSIM service')}
          onCancel={() => setPendingPayer(undefined)}
          onConfirm={() => submitPayerChange(pendingPayer)}
        >
          <p>
            {t.LC2W(
              'Are you sure you want to change the payer for the KelloSIM service? This change will affect all new KelloSIM subscriptions.'
            )}
          </p>
        </ConfirmationModal>
      )}
    </div>
  );
};

export const KelloSimBillingModelSetting = ({ companyInfos }: KelloSimBillingModelSettingProps) => {
  return (
    <>
      <p>
        {t.FYJ7(
          'KelloSIM enables the use of an Apple Watch smartwatch for calls, messages, mobile data and notifications, even when the iPhone is not present.'
        )}
      </p>
      <p>
        <I18nTextLinkEmbedded
          translation={t.FHAL(
            'You can choose whether the employee pays for the service themselves, the company offers the service to the employee, or the service is blocked from employees entirely. The employee activates the KelloSIM service in Employee OmaElisa through the Apple Watch app. [More information about setup.]'
          )}
          linkHref="https://yrityksille.elisa.fi/ohje/elisa-kellosim"
        />
      </p>
      {[...companyInfos]
        .sort((a, b) => (a.companyName || '').localeCompare(b.companyName || '', 'fi', { sensitivity: 'accent' }))
        .map(companyInfo => (
          <KelloSimBillingModelCompanySetting
            key={companyInfo.mdmId}
            companyInfo={companyInfo}
            showCompanyName={companyInfos.length > 1}
          />
        ))}
    </>
  );
};
